import { InputBase } from '@mantine/core';
import type { ReactNode } from 'react';
import { IMaskInput } from 'react-imask';

const ALPHANUMERIC = { '#': /[A-Za-z0-9]/ } as const;
const toUpperCase = (value: string) => value.toUpperCase();

// IBAN or local account number (RIB, for countries outside the IBAN zone):
// alphanumeric grouped in fours, uppercased live. Up to 34 characters (the ISO
// maximum); the backend decides which shape the company may use.
export const IBAN_MASK = {
  mask: '#### #### #### #### #### #### #### #### ##',
  definitions: ALPHANUMERIC,
  prepare: toUpperCase,
} as const;

// BIC: six letters (bank + country) then two, optionally five, alphanumeric.
export const BIC_MASK = {
  mask: 'aaaaaa##[###]',
  definitions: ALPHANUMERIC,
  prepare: toUpperCase,
} as const;

type MaskConfig = typeof IBAN_MASK | typeof BIC_MASK;

type MaskedTextInputProps = {
  maskConfig: MaskConfig;
  value: string;
  onChange?: (value: string) => void;
  label?: ReactNode;
  placeholder?: string;
  error?: ReactNode;
  disabled?: boolean;
  flex?: number;
};

// Wraps a Mantine input around react-imask (the mask library Mantine documents).
// `unmask` keeps the form value free of the grouping spaces, so the value that
// round-trips to the backend and drives the dirty check is the raw IBAN/BIC.
// The mask config is spread as a literal so react-imask resolves it to a pattern
// mask (a widened `string` would break its discriminated-union prop typing).
export const MaskedTextInput = ({
  maskConfig,
  value,
  onChange,
  label,
  placeholder,
  error,
  disabled,
  flex,
}: MaskedTextInputProps) => (
  <InputBase
    component={IMaskInput}
    {...maskConfig}
    unmask
    value={value}
    onAccept={(accepted: string) => onChange?.(accepted)}
    label={label}
    placeholder={placeholder}
    error={error}
    disabled={disabled}
    flex={flex}
  />
);
